import { Container } from '@cleartrip/ct-design-components'
import React from 'react'

import { SelectedFareCardPatternConfig } from '@/constants/flights/premiumEconomy'

/** Quote CDN urls — Rukmini `f_auto,q_auto` commas break unquoted CSS `url()`. */
const cssUrl = (src: string) => `url("${src}")`

type SelectedFareCardPatternProps = {
    pattern: SelectedFareCardPatternConfig
}

/**
 * Decorative motif behind a selected themed fare card.
 *
 * Premium Economy / Business (Figma 512:11443) paint the arc PNG into a
 * bottom-right layer; First Class (Figma 1724:71747) masks the diamond lattice
 * over a champagne gradient in a top-right layer.
 *
 * The outer box positions and clips the layer, so the inner box can carry the
 * Y-flip without also rotating the card-space `fade`.
 */
const SelectedFareCardPattern: React.FC<SelectedFareCardPatternProps> = ({ pattern }) => {
    const {
        mode,
        src,
        gradient,
        opacity,
        widthPx,
        heightPx,
        offsetRightPx,
        offsetTopPx,
        offsetBottomPx,
        backgroundSize,
        backgroundPosition,
        scaleY,
        fade,
        filter,
    } = pattern

    const artwork =
        mode === 'mask'
            ? {
                  background: gradient,
                  WebkitMaskImage: cssUrl(src),
                  maskImage: cssUrl(src),
                  WebkitMaskSize: backgroundSize,
                  maskSize: backgroundSize,
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskPosition: backgroundPosition,
                  maskPosition: backgroundPosition,
              }
            : {
                  backgroundImage: cssUrl(src),
                  backgroundRepeat: 'no-repeat',
                  backgroundSize,
                  backgroundPosition,
              }

    return (
        <Container
            aria-hidden
            css={{
                position: 'absolute',
                right: `${offsetRightPx}px`,
                ...(typeof offsetTopPx === 'number' ? { top: `${offsetTopPx}px` } : {}),
                ...(typeof offsetBottomPx === 'number' ? { bottom: `${offsetBottomPx}px` } : {}),
                width: `${widthPx}px`,
                height: `${heightPx}px`,
                ...(fade
                    ? {
                          WebkitMaskImage: fade,
                          maskImage: fade,
                          WebkitMaskSize: '100% 100%',
                          maskSize: '100% 100%',
                          WebkitMaskRepeat: 'no-repeat',
                          maskRepeat: 'no-repeat',
                      }
                    : {}),
                pointerEvents: 'none',
                zIndex: 0,
                overflow: 'hidden',
            }}
        >
            <Container
                css={{
                    width: '100%',
                    height: '100%',
                    opacity,
                    ...artwork,
                    transform: scaleY === -1 ? 'scaleY(-1)' : undefined,
                    ...(filter ? { filter } : {}),
                }}
            />
        </Container>
    )
}

export default SelectedFareCardPattern
